import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';

// Icons
const StarIcon = ({ filled }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const PendingReviewModal = () => {
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [pendingReview, setPendingReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelayOptions, setShowDelayOptions] = useState(false);
  const [delayLoading, setDelayLoading] = useState(false);

  // Check for pending reviews
  const checkPendingReviews = useCallback(async () => {
    if (!user || !activeProfile?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data: fallbackData } = await supabase
        .from('trade_schedules')
        .select(`
          id,
          trade_id,
          selected_date,
          selected_time,
          review_delay_until,
          review_delay_used,
          locations (id, store_name),
          trade_requests (
            id,
            requester_id,
            card_owner_id,
            status,
            requester:public_profiles!trade_requests_requester_id_fkey (username, avatar_url),
            card_owner:public_profiles!trade_requests_card_owner_id_fkey (username, avatar_url)
          )
        `)
        .eq('status', 'confirmed');

      // Filter to schedules where this user is involved in the trade
      // trade_requests may be an object or array depending on FK direction
      const relevant = (fallbackData || []).filter(s => {
        const tr = Array.isArray(s.trade_requests) ? s.trade_requests[0] : s.trade_requests;
        return tr && (tr.requester_id === activeProfile.id || tr.card_owner_id === activeProfile.id);
      });

      if (relevant.length > 0) {
        for (const schedule of relevant) {
          const { data: existingReview } = await supabase
            .from('trade_reviews')
            .select('id')
            .eq('trade_schedule_id', schedule.id)
            .eq('reviewer_id', activeProfile.id)
            .maybeSingle();


          if (!existingReview) {
            // Skip if already reviewed this session
            if (sessionStorage.getItem(`reviewed_${schedule.id}`)) continue;

            if (schedule.review_delay_until) {
              const delayUntil = new Date(schedule.review_delay_until);
              if (new Date() < delayUntil) continue;
            }

            // Parse date that may be "3/21" or "3/21/2026"
            let tradeTime = null;
            const dateParts = (schedule.selected_date || '').split('/');
            if (dateParts.length >= 2) {
              const month = parseInt(dateParts[0]) - 1;
              const day = parseInt(dateParts[1]);
              const year = dateParts[2] ? parseInt(dateParts[2]) : new Date().getFullYear();
              // Parse time like "3:00 PM"
              const timeMatch = (schedule.selected_time || '').match(/(\d+):(\d+)\s*(AM|PM)/i);
              if (timeMatch) {
                let hours = parseInt(timeMatch[1]);
                const mins = parseInt(timeMatch[2]);
                const period = timeMatch[3].toUpperCase();
                if (period === 'PM' && hours !== 12) hours += 12;
                if (period === 'AM' && hours === 12) hours = 0;
                tradeTime = new Date(year, month, day, hours, mins);
              } else {
                tradeTime = new Date(year, month, day);
              }
            }
            const threeHoursAgo = new Date(Date.now() - 3 * 60 * 60 * 1000);
            if (!tradeTime || tradeTime > threeHoursAgo) continue;

            const tradeReq = Array.isArray(schedule.trade_requests) ? schedule.trade_requests[0] : schedule.trade_requests;
            const isRequester = tradeReq.requester_id === activeProfile.id;
            const otherUser = isRequester ? tradeReq.card_owner : tradeReq.requester;

            setPendingReview({
              trade_id: tradeReq.id,
              schedule_id: schedule.id,
              other_user_id: isRequester ? tradeReq.card_owner_id : tradeReq.requester_id,
              other_username: otherUser?.username || 'Trader',
              other_avatar: otherUser?.avatar_url,
              location_id: schedule.locations?.id,
              location_name: schedule.locations?.store_name || 'Location',
              can_delay: !schedule.review_delay_used
            });
            setLoading(false);
            return;
          }
        }
      }
      setPendingReview(null);
    } catch (err) {
      console.error('[PendingReview] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [user, activeProfile?.id]);

  useEffect(() => {
    checkPendingReviews();

    // Re-check every 30 seconds (in case delay expires)
    const interval = setInterval(checkPendingReviews, 30000);
    return () => clearInterval(interval);
  }, [checkPendingReviews]);

  // Handle delay selection
  const handleDelay = async (minutes) => {
    if (!pendingReview || !activeProfile?.id) return;

    setDelayLoading(true);
    try {
      // Use the database function
      const { data, error } = await supabase.rpc('set_review_delay', {
        p_schedule_id: pendingReview.schedule_id,
        p_user_id: activeProfile.id,
        p_delay_minutes: minutes
      });

      if (error) {
        console.error('[PendingReview] Delay RPC error:', error);
        // Fallback to direct update
        await supabase
          .from('trade_schedules')
          .update({
            review_delay_until: new Date(Date.now() + minutes * 60 * 1000).toISOString(),
            review_delay_used: true
          })
          .eq('id', pendingReview.schedule_id);
      }

      // Hide modal until delay expires
      setPendingReview(null);
      setShowDelayOptions(false);
    } catch (err) {
      console.error('[PendingReview] Error setting delay:', err);
    } finally {
      setDelayLoading(false);
    }
  };

  // Handle review now — mark as dismissed so it doesn't reappear after submission
  const handleReviewNow = () => {
    if (!pendingReview) return;
    sessionStorage.setItem(`reviewed_${pendingReview.schedule_id}`, 'true');
    navigate(`/trade/${pendingReview.trade_id}/review`);
    setPendingReview(null);
  };

  // Don't show anything while loading or if no pending review
  if (loading || !pendingReview) {
    return null;
  }

  const delayOptions = [
    { label: '5 minutes', minutes: 5 },
    { label: '15 minutes', minutes: 15 },
    { label: '30 minutes', minutes: 30 },
    { label: '1 hour', minutes: 60 },
  ];

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      zIndex: 99999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    },
    modal: {
      backgroundColor: theme.bgCard,
      borderRadius: '16px',
      maxWidth: '400px',
      width: '100%',
      padding: '24px',
      textAlign: 'center',
    },
    avatar: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: theme.bgSecondary,
      margin: '0 auto 16px',
      overflow: 'hidden',
    },
    avatarImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    title: {
      fontSize: '22px',
      fontWeight: '600',
      color: theme.text,
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '15px',
      color: theme.textSecondary,
      marginBottom: '24px',
    },
    location: {
      fontSize: '13px',
      color: theme.textMuted,
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
    },
    button: {
      width: '100%',
      padding: '14px 24px',
      fontSize: '16px',
      fontWeight: '600',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      marginBottom: '12px',
    },
    primaryButton: {
      backgroundColor: '#8b5cf6',
      color: '#fff',
    },
    delayButton: {
      backgroundColor: theme.bgSecondary,
      color: theme.textSecondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    delayOptions: {
      marginTop: '16px',
    },
    delayOption: {
      width: '100%',
      padding: '12px',
      fontSize: '14px',
      fontWeight: '500',
      backgroundColor: theme.bgSecondary,
      color: theme.text,
      border: `1px solid ${theme.border}`,
      borderRadius: '8px',
      cursor: 'pointer',
      marginBottom: '8px',
    },
    note: {
      fontSize: '12px',
      color: theme.textMuted,
      marginTop: '16px',
    },
    stars: {
      display: 'flex',
      justifyContent: 'center',
      gap: '4px',
      color: '#fbbf24',
      marginBottom: '16px',
    },
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.avatar}>
          {pendingReview.other_avatar ? (
            <img src={pendingReview.other_avatar} alt="" style={styles.avatarImg} />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              color: theme.textMuted,
            }}>
              {pendingReview.other_username?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
        </div>

        <div style={styles.stars}>
          {[1, 2, 3, 4, 5].map(i => (
            <StarIcon key={i} filled={false} />
          ))}
        </div>

        <h2 style={styles.title}>How was your trade?</h2>
        <p style={styles.subtitle}>
          Leave a review for your trade with <strong>@{pendingReview.other_username}</strong>
        </p>

        <div style={styles.location}>
          at {pendingReview.location_name}
        </div>

        <button
          style={{ ...styles.button, ...styles.primaryButton }}
          onClick={handleReviewNow}
        >
          Review Now
        </button>

        {pendingReview.can_delay && !showDelayOptions && (
          <button
            style={{ ...styles.button, ...styles.delayButton }}
            onClick={() => setShowDelayOptions(true)}
          >
            <ClockIcon />
            Remind Me Later
          </button>
        )}

        {showDelayOptions && (
          <div style={styles.delayOptions}>
            <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '12px' }}>
              Choose when to be reminded:
            </p>
            {delayOptions.map(opt => (
              <button
                key={opt.minutes}
                style={styles.delayOption}
                onClick={() => handleDelay(opt.minutes)}
                disabled={delayLoading}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {!pendingReview.can_delay && (
          <p style={styles.note}>
            Reviews help keep our trading community safe and trustworthy.
          </p>
        )}
      </div>
    </div>
  );
};

export default PendingReviewModal;
